import { AsyncZipDeflate, Zip, ZipDeflate } from "fflate";

/** Compress one file at a time. Keep originals intact if packing is interrupted. */
export async function createTransactionBatchZip(files: Record<string, Uint8Array>, signal: AbortSignal, progress: (done: number, total: number) => void): Promise<Blob> {
	signal.throwIfAborted();
	const parts: BlobPart[] = [];
	let finish!: (blob: Blob) => void;
	let fail!: (error: unknown) => void;
	let rejectFile: ((error: unknown) => void) | undefined;
	const completed = new Promise<Blob>((resolve, reject) => { finish = resolve; fail = reject; });
	// The archive may fail while we are awaiting the current file.
	void completed.catch(() => {});
	const archive = new Zip((error, chunk, final) => {
		if (error) { fail(error); rejectFile?.(error); return; }
		parts.push(new Uint8Array(chunk).buffer);
		if (final) finish(new Blob(parts, { type: "application/zip" }));
	});
	const abort = () => { archive.terminate(); rejectFile?.(signal.reason); fail(signal.reason); };
	signal.addEventListener("abort", abort, { once: true });
	try {
		const entries = Object.entries(files);
		for (let index = 0; index < entries.length; index++) {
			signal.throwIfAborted();
			const [path, data] = entries[index];
			await new Promise<void>((resolve, reject) => {
				rejectFile = reject;
				const file = data.byteLength >= 64 * 1024 ? new AsyncZipDeflate(path, { level: 6 }) : new ZipDeflate(path, { level: 6 });
				archive.add(file);
				const forward = file.ondata!;
				file.ondata = (error, chunk, final) => {
					forward(error, chunk, final);
					if (error) reject(error);
					else if (final) resolve();
				};
				file.push(data.byteLength >= 64 * 1024 ? data.slice() : data, true);
			});
			rejectFile = undefined;
			progress(index + 1, entries.length);
			if (index % 20 === 0) await new Promise(resolve => setTimeout(resolve, 0));
		}
		signal.throwIfAborted();
		archive.end();
		return await completed;
	} catch (error) {
		archive.terminate();
		throw error;
	} finally {
		signal.removeEventListener("abort", abort);
	}
}
